import React, { useState, useCallback, useEffect } from 'react';
import { Modal, message } from 'antd';
import { PayrollPeriod } from '../../../../types/hr';
import validationService from '../../../../services/validationService';
import {
  ValidationConstraint,
  ConditionalValidationCriteria,
  ValidationReport
} from '../../../../types/validation';
import TableauCalculsValidation from './TableauCalculsValidation';
import ModalValidationIndividuelle from './ModalValidationIndividuelle';
import ModalValidationLot from './ModalValidationLot';
import RapportValidation from './RapportValidation';

interface ValidationModalProps {
  visible: boolean;
  period: PayrollPeriod | null;
  onCancel: () => void;
  onSuccess?: () => void;
}

const ValidationModal: React.FC<ValidationModalProps> = ({
  visible,
  period,
  onCancel,
  onSuccess
}) => {
  const [selectedCalculation, setSelectedCalculation] = useState<any>(null);
  const [selectedCalculations, setSelectedCalculations] = useState<number[]>([]);
  const [validationReport, setValidationReport] = useState<ValidationReport | null>(null);
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setSelectedCalculation(null); setSelectedCalculations([]); setValidationReport(null);
    setShowIndividualModal(false); setShowBatchModal(false); setShowReportModal(false);
  }, [visible, period?.id, period?.company_id]);

  const handleValidationChange = useCallback(() => {
    onSuccess?.();
  }, [onSuccess]);

  const handleShowValidationModal = (calculation: any) => {
    setSelectedCalculation(calculation);
    setShowIndividualModal(true);
  };

  const handleShowBatchModal = (selectedIds: number[]) => {
    setSelectedCalculations(selectedIds);
    setShowBatchModal(true);
  };

  const handleShowReport = async () => {
    if (!period?.id) return;
    
    try {
      setLoading(true);
      const report = await validationService.generateValidationReport(period.id);
      setValidationReport(report);
      setShowReportModal(true);
    } catch (error) {
      console.error('Erreur lors de la génération du rapport:', error);
      message.error('Erreur lors de la génération du rapport');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCalculation = async (
    calculationId: number, 
    constraints?: ValidationConstraint[]
  ) => {
    try {
      setLoading(true);
      await validationService.validateCalculation(calculationId, constraints);
      message.success('Calcul validé avec succès');
      handleValidationChange();
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      message.error('Erreur lors de la validation');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleUnvalidateCalculation = async (calculationId: number) => {
    try {
      setLoading(true);
      await validationService.unvalidateCalculation(calculationId);
      message.success('Validation annulée avec succès');
      handleValidationChange();
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
      message.error('Erreur lors de l\'annulation de la validation');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleBatchValidation = async (
    calculationIds: number[],
    constraints?: ValidationConstraint[]
  ) => {
    try {
      setLoading(true);
      await validationService.validateMultipleCalculations(calculationIds, constraints);
      message.success(`${calculationIds.length} calcul(s) validé(s) avec succès`);
      handleValidationChange();
    } catch (error) {
      console.error('Erreur lors de la validation en lot:', error);
      message.error('Erreur lors de la validation en lot');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleConditionalValidation = async (
    criteria: ConditionalValidationCriteria,
    constraints?: ValidationConstraint[]
  ) => {
    if (!period?.id) throw new Error('Période non définie');
    
    try {
      setLoading(true);
      const result = await validationService.conditionalValidation(period.id, {
        criteria,
        constraints,
        validate_on_success: true
      });
      message.success(`${result.matched_calculations} calcul(s) traité(s)`);
      handleValidationChange();
      return result;
    } catch (error) {
      console.error('Erreur lors de la validation conditionnelle:', error);
      message.error('Erreur lors de la validation conditionnelle');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshReport = async () => {
    if (!period?.id) return;
    
    try {
      setLoading(true);
      const report = await validationService.generateValidationReport(period.id);
      setValidationReport(report);
      message.success('Rapport actualisé');
    } catch (error) {
      console.error('Erreur lors de l\'actualisation:', error);
      message.error('Erreur lors de l\'actualisation du rapport');
    } finally {
      setLoading(false);
    }
  };

  if (!period) return null;

  return (
    <>
      <Modal
        title={`Validation des Calculs - ${period.name}`}
        open={visible}
        onCancel={onCancel}
        width={1200}
        footer={null}
        destroyOnClose
      >
        <TableauCalculsValidation
          periodId={period.id!}
          companyId={period.company_id}
          onValidationChange={handleValidationChange}
          onShowValidationModal={handleShowValidationModal}
          onShowBatchModal={handleShowBatchModal}
          onShowReport={handleShowReport}
        />
      </Modal>

      {/* Modal de validation individuelle */}
      <ModalValidationIndividuelle
        visible={showIndividualModal}
        calculation={selectedCalculation}
        onCancel={() => {
          setShowIndividualModal(false);
          setSelectedCalculation(null);
        }}
        onValidate={handleValidateCalculation}
        onUnvalidate={handleUnvalidateCalculation}
        loading={loading}
      />

      {/* Modal de validation en lot */}
      <ModalValidationLot
        visible={showBatchModal}
        selectedCalculations={selectedCalculations}
        onCancel={() => {
          setShowBatchModal(false);
          setSelectedCalculations([]);
        }}
        onValidate={handleBatchValidation}
        onConditionalValidate={handleConditionalValidation}
        loading={loading}
      />

      {/* Modal de rapport de validation */}
      <RapportValidation
        visible={showReportModal}
        report={validationReport}
        onCancel={() => {
          setShowReportModal(false);
          setValidationReport(null);
        }}
        onRefresh={handleRefreshReport}
        loading={loading}
      />
    </>
  );
};

export default ValidationModal;
